import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { userState } from "../features/user/userSlice";
import useTokenExpiration from "../hooks/useTokenExpiration";
import { extractUserFromToken } from "../uitls/Utils";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const userInformation = useSelector(userState);
  const lastTokenRef = useRef(null);

  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("token");
    
    if (!token || token === "null" || token === "undefined") {
      console.log("🔄 Initializing Auth: No valid token found");
      return {
        token: null,
        user: null,
        roleId: null,
        expired: false,
        mfaVerified: false,
        mfaRequired: false,
      };
    }

    let user = null;
    try {
      const storedUser = localStorage.getItem("user");
      user = storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
      localStorage.removeItem("user");
    }
    
    const roleId = localStorage.getItem("roleId");
    const mfaVerified = localStorage.getItem("mfaVerified") === 'true';
    const mfaRequired = localStorage.getItem("mfaRequired") === 'true';

    if (token && !user) {
      console.log("User is null but token exists. Extracting user from token...");
      user = extractUserFromToken(token);
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }
    }

    if (token && (!user || !roleId)) {
      console.warn("Incomplete auth data detected. Clearing localStorage.");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("roleId");
      localStorage.removeItem("mfaVerified");
      localStorage.removeItem("mfaRequired");
      lastTokenRef.current = null;
      return {
        token: null,
        user: null,
        roleId: null,
        expired: false,
        mfaVerified: false,
        mfaRequired: false,
      };
    }

    lastTokenRef.current = token;
    
    return {
      token: token,
      user: user,
      roleId: roleId,
      expired: false,
      mfaVerified: mfaVerified,
      mfaRequired: mfaRequired,
    };
  });

  const handleTokenExpire = useCallback(() => {
    setAuth({
      token: null,
      user: null,
      roleId: null,
      expired: true,
      mfaVerified: false,
      mfaRequired: false,
    });
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("roleId");
    localStorage.removeItem("mfaVerified");
    localStorage.removeItem("mfaRequired");
  }, []);

  useEffect(() => {
    if (!userInformation?.token && auth?.token) {
      console.log("🔄 Sync: Redux has no token but AuthProvider does - clearing auth");
      setAuth({
        token: null,
        user: null,
        roleId: null,
        expired: false,
        mfaVerified: false,
        mfaRequired: false,
      });
      return;
    }

    if (userInformation?.token) {
      if (userInformation.token !== lastTokenRef.current) {
        lastTokenRef.current = userInformation.token;
      }

      let userData = userInformation.user;

      if (!userData && userInformation.token) {
        console.log("User is null in Redux state. Extracting from token...");
        userData = extractUserFromToken(userInformation.token);
        if (userData) {
          localStorage.setItem("user", JSON.stringify(userData));
        }
      }

      const mfaVerified = userInformation.mfaVerified || false;
      const mfaRequired = userInformation.mfaRequired || false;

      setAuth({
        token: userInformation.token,
        user: userData,
        roleId: userInformation.roleId || userData?.roleId,
        expired: false,
        mfaVerified,
        mfaRequired,
      });

      if (userInformation.token) {
        localStorage.setItem("token", userInformation.token);
      }
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
      if (userInformation.roleId) {
        localStorage.setItem("roleId", userInformation.roleId);
      }
      localStorage.setItem("mfaVerified", mfaVerified.toString());
      localStorage.setItem("mfaRequired", mfaRequired.toString());
    }
    else if (userInformation?.token === null && userInformation?.user === null) {
      setAuth({
        token: null,
        user: null,
        roleId: null,
        expired: false,
        mfaVerified: false,
        mfaRequired: false,
      });
    }
  }, [
    userInformation?.token, 
    userInformation?.user, 
    userInformation?.roleId, 
    userInformation?.mfaVerified, 
    userInformation?.mfaRequired
  ]);

  useTokenExpiration(auth.expired ? null : auth.token, handleTokenExpire);

  const contextValue = useMemo(
    () => ({
      auth,
      setAuth,
    }),
    [auth]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;